import type { PartnerSeason } from '../types';

export type SeasonAssignmentSuggestion = {
  termId: string;
  confidence: 'inside_window' | 'near_window';
  reason: string;
};

const DAY_MS = 86400000;
const ymdTime = (value: string) => new Date(`${value}T00:00:00.000Z`).getTime();

/** Start date is the primary assignment fact; end date is validation only. */
export function inferSeasonFromStartDate(
  startDate: string | null | undefined,
  seasons: PartnerSeason[],
  toleranceDays = 21,
): SeasonAssignmentSuggestion | null {
  if (!startDate || !/^\d{4}-\d{2}-\d{2}$/.test(startDate)) return null;
  const exact = seasons.filter((season) => season.defaultProgramStartDate && season.defaultProgramEndDate
    && startDate >= season.defaultProgramStartDate && startDate <= season.defaultProgramEndDate);
  if (exact.length === 1) return { termId: exact[0].termId, confidence: 'inside_window', reason: `Start date ${startDate} falls inside ${exact[0].label || exact[0].termId}.` };
  if (exact.length > 1) return null;

  const startMs = ymdTime(startDate);
  const nearby = seasons.flatMap((season) => {
    if (!season.defaultProgramStartDate || !season.defaultProgramEndDate) return [];
    const distance = Math.min(Math.abs(startMs - ymdTime(season.defaultProgramStartDate)), Math.abs(startMs - ymdTime(season.defaultProgramEndDate))) / DAY_MS;
    return distance <= toleranceDays ? [{ season, distance }] : [];
  }).sort((a, b) => a.distance - b.distance);
  if (!nearby.length || (nearby[1] && nearby[0].distance === nearby[1].distance)) return null;
  return { termId: nearby[0].season.termId, confidence: 'near_window', reason: `Start date ${startDate} is ${Math.round(nearby[0].distance)} days from ${nearby[0].season.label || nearby[0].season.termId}.` };
}
